import {
  Box,
  Circle,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  ScaleFade,
  Tooltip,
  usePrefersReducedMotion,
  useDisclosure,
} from '@chakra-ui/react';
import { Globe } from 'lucide-react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { iconSize, pxToRem } from '@duke-hq/ui/constants/scalable';
import { ToolbarIconButton } from '../../navbar/ToolbarIconButton';
import { TranslationsPanel } from './TranslationsPanel';
import {
  BREATH_ANIMATION,
  BREATH_KEYFRAMES,
  KICKOFF_RING_ANIMATION,
  TOOLBAR_DOT_TINT_TRANSITION,
} from './translationsMotion';
import {
  useContentTranslations,
  type TranslatableResourceKind,
  type TranslationPillSummary,
} from './useContentTranslations';
import { coverageColorScheme, coverageLabel } from './coverageStatus';
import { useTransitionCounter } from './useTransitionCounter';
import { testIds } from './testIds';

function statusDotColor(pill: TranslationPillSummary): string {
  if (pill.kind === 'unknown') {
    return 'gray.400';
  }
  return `${coverageColorScheme(pill)}.500`;
}

function summaryTooltip(pill: TranslationPillSummary): string {
  if (pill.kind === 'unknown') {
    return t`Translations: status unavailable`;
  }
  return t`Translations: ${coverageLabel(pill)}`;
}

function isKickoff(
  previous: TranslationPillSummary['kind'] | null,
  next: TranslationPillSummary['kind'],
): boolean {
  return previous !== 'in-progress' && next === 'in-progress';
}

// Presentational, so Storybook can render it across states without tRPC.
export function TranslationsTrigger({ pill }: { pill: TranslationPillSummary }) {
  const motionDisabled = usePrefersReducedMotion();
  const dotColor = statusDotColor(pill);
  // Only in-flight work breathes; a needs-attention dot sits steady so it never nags.
  const dotBreathes = !motionDisabled && pill.kind === 'in-progress';
  const kickoffKey = useTransitionCounter(pill.kind, isKickoff, !motionDisabled);

  return (
    <Tooltip label={summaryTooltip(pill)}>
      <Box position="relative" display="inline-flex">
        <ToolbarIconButton
          aria-label={summaryTooltip(pill)}
          aria-haspopup="dialog"
          data-testid={testIds.toolbarPill}
        >
          <Globe size={iconSize.md} />
        </ToolbarIconButton>
        {!motionDisabled && kickoffKey > 0 ? (
          <Box
            key={kickoffKey}
            position="absolute"
            top={0.5}
            right={0.5}
            boxSize={pxToRem(10)}
            borderRadius="full"
            bg="blue.400"
            pointerEvents="none"
            aria-hidden
            sx={{
              animation: KICKOFF_RING_ANIMATION,
              '@keyframes translationKickoffRing': {
                '0%': { transform: 'scale(0.6)', opacity: 0.5 },
                '100%': { transform: 'scale(2.4)', opacity: 0 },
              },
            }}
          />
        ) : null}
        <ScaleFade in initialScale={0.4}>
          <Circle
            position="absolute"
            top={0.5}
            right={0.5}
            size={pxToRem(10)}
            bg={dotColor}
            borderWidth={pxToRem(2)}
            borderColor="bg.surface"
            transition={motionDisabled ? 'none' : TOOLBAR_DOT_TINT_TRANSITION}
            pointerEvents="none"
            aria-hidden
            sx={dotBreathes ? { animation: BREATH_ANIMATION, ...BREATH_KEYFRAMES } : undefined}
          />
        </ScaleFade>
      </Box>
    </Tooltip>
  );
}

export function TranslationsToolbarPill({
  resourceId,
  resourceVersionId,
  resourceKind,
  publishedVersion,
  onViewLanguage,
  onEditLanguage,
}: {
  // Overrides key to the resource, not a version, so they survive republishes.
  resourceId: string | null | undefined;
  resourceVersionId: number | null | undefined;
  resourceKind: TranslatableResourceKind;
  publishedVersion?: number | null;
  onViewLanguage?: (language: string) => void;
  onEditLanguage?: (language: string) => void;
}) {
  if (resourceVersionId == null || resourceId == null) {
    return null;
  }

  return (
    <TranslationsToolbarPillContent
      resourceVersionId={resourceVersionId}
      resourceKind={resourceKind}
      publishedVersion={publishedVersion}
      onViewLanguage={onViewLanguage}
      onEditLanguage={onEditLanguage}
    />
  );
}

function TranslationsToolbarPillContent({
  resourceVersionId,
  resourceKind,
  publishedVersion,
  onViewLanguage,
  onEditLanguage,
}: {
  resourceVersionId: number;
  resourceKind: TranslatableResourceKind;
  publishedVersion?: number | null;
  onViewLanguage?: (language: string) => void;
  onEditLanguage?: (language: string) => void;
}) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const motionDisabled = usePrefersReducedMotion();
  const translations = useContentTranslations({
    resourceVersionId,
    resourceKind,
    isPanelOpen: isOpen,
  });

  const viewLabel =
    resourceKind === 'PAGE' ? t`View translation on the page` : t`View translation on the canvas`;

  return (
    <Popover placement="bottom-end" isOpen={isOpen} onOpen={onOpen} onClose={onClose}>
      <PopoverTrigger>
        <Box>
          <TranslationsTrigger pill={translations.pill} />
        </Box>
      </PopoverTrigger>
      <PopoverContent
        width={pxToRem(320)}
        transformOrigin="top right"
        motionProps={motionDisabled ? { transition: { duration: 0 } } : undefined}
      >
        <PopoverArrow />
        <PopoverHeader fontSize="sm" fontWeight="semibold">
          <Trans>Translations</Trans>
        </PopoverHeader>
        <PopoverBody p={0}>
          <TranslationsPanel
            resourceVersionId={resourceVersionId}
            resourceKind={resourceKind}
            publishedVersion={publishedVersion}
            translations={translations}
            onViewLanguage={
              onViewLanguage == null
                ? undefined
                : (language) => {
                    onClose();
                    onViewLanguage(language.code);
                  }
            }
            viewLabel={viewLabel}
            // Only once a published version exists: nothing to extract before then.
            onEditLanguage={
              publishedVersion == null || onEditLanguage == null
                ? undefined
                : (language) => {
                    onClose();
                    onEditLanguage(language.code);
                  }
            }
          />
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}
